import {
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  XCircleIcon,
  ClockIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon
} from "@heroicons/react/24/outline";
import { Button, Card, CardBody, Menu, MenuHandler, MenuItem, MenuList, Typography } from "@material-tailwind/react";
import { getPhoneNumbers } from "api/phoneNumbers";
import { CardEmptyState } from "components/Cards/CardEmptyState";
import { CardLoadingState } from "components/Cards/CardLoadingState";
import Table from "components/Table";
import { useFetch } from "hooks/useFetch";
import { useEffect, useRef, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { routes } from "routes";
import { Account, PhoneNumbersTabType } from "types";
import { CardBackground } from "components/Cards/CardBackground";
import { displayBoolean } from "util/text";

const tabs: {
  value: PhoneNumbersTabType;
  label: string;
  Icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> & {
      title?: string | undefined;
      titleId?: string | undefined;
    } & React.RefAttributes<SVGSVGElement>
  >;
}[] = [
  { value: "submitted", label: "Submitted", Icon: ClipboardDocumentCheckIcon },
  { value: "pending", label: "Pending", Icon: ClockIcon },
  { value: "inReview", label: "In Review", Icon: MagnifyingGlassIcon },
  { value: "failed", label: "Failed", Icon: XCircleIcon },
  { value: "free", label: "Free", Icon: CurrencyDollarIcon },
  { value: "ignore", label: "Ignore", Icon: NoSymbolIcon }
];

const PhoneNumbersTable = ({
  tab,
  data,
  isLoading
}: {
  tab: PhoneNumbersTabType;
  data: Account[] | null;
  isLoading: boolean;
}) => {
  const navigate = useNavigate();

  const getTableDefinition = (tab: PhoneNumbersTabType) => {
    const tableDef = [
      { header: "Name", accessor: account => account.name },
      { header: "Phone Number", accessor: account => account?.phoneConfig?.phoneNumber },
      { header: "Plan", accessor: account => account.plan },
      { header: "Auto Pay", accessor: account => displayBoolean(account.autoPay) },
      { header: "Status", accessor: account => account?.phoneConfig?.status },
      {
        header: "Actions",
        accessor: account => (
          <Button
            onClick={() => {
              navigate({
                pathname: routes.app.accounts.path,
                search: createSearchParams({ accountId: account.id }).toString()
              });
            }}
            variant="text"
            color="blue"
          >
            View
          </Button>
        )
      }
    ];
    if (tab === "submitted") {
      tableDef.unshift({
        header: "Submitted Date",
        accessor: account => account?.brandRegistration?.submissionDate?.toDate().toLocaleDateString()
      });
    } else if (tab === "pending") {
      tableDef.unshift({
        header: "Submitted to Carrier",
        accessor: account => account?.phoneConfig?.pendingDate?.toDate().toLocaleDateString()
      });
    } else if (tab === "failed") {
      tableDef.unshift({
        header: "Failed Date",
        accessor: account => account?.phoneConfig?.failedDate?.toDate().toLocaleDateString()
      });
    } else if (tab === "inReview") {
      tableDef.unshift({
        header: "Review Date",
        accessor: account => account?.phoneConfig?.pendingDate?.toDate().toLocaleDateString()
      });
    } else if (tab === "free") {
      tableDef.unshift({
        header: "Expired Date",
        accessor: account => account?.renewalDate?.toDate().toLocaleDateString()
      });
    } else if (tab === "ignore") {
      tableDef.unshift({
        header: "Ignored Date",
        accessor: account => account?.phoneConfig?.pendingDate?.toDate().toLocaleDateString()
      });
    }
    return tableDef;
  };

  if (isLoading) {
    return <CardLoadingState />;
  }

  if (!data?.length) {
    return <CardEmptyState message="No numbers found" />;
  }

  return (
    <Table
      items={data}
      onRowDoubleClick={account => {
        navigate({
          pathname: routes.app.accounts.path,
          search: createSearchParams({ accountId: account.id }).toString()
        });
      }}
      columns={getTableDefinition(tab)}
    />
  );
};

const CustomDropdown = ({
  tab,
  setTab,
  tabs,
  subtitle,
  title
}: {
  tab: PhoneNumbersTabType;
  setTab: (tab: PhoneNumbersTabType) => void;
  tabs: {
    value: PhoneNumbersTabType;
    label: string;
    Icon: React.ForwardRefExoticComponent<
      Omit<React.SVGProps<SVGSVGElement>, "ref"> & {
        title?: string | undefined;
        titleId?: string | undefined;
      } & React.RefAttributes<SVGSVGElement>
    >;
  }[];
  subtitle?: string;
  title: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    const currentIndex = tabs.findIndex(t => t.value === tab);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % tabs.length;
      setTab(tabs[nextIndex].value);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      setTab(tabs[prevIndex].value);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const currentTab = tabs.find(t => t.value === tab);
  const CurrentIcon = currentTab?.Icon || tabs[0].Icon;

  return (
    <div className="mb-10 flex flex-wrap items-center justify-between gap-6">
      <div className="flex items-center gap-6">
        <div>
          <Typography variant="h5" color="blue-gray" className="mb-1">
            {title}
          </Typography>
          <Typography variant="small" className="font-normal text-blue-gray-600">
            {subtitle || ""}
          </Typography>
        </div>
      </div>
      <div className="ml-auto" ref={menuRef}>
        <Menu open={isOpen} handler={setIsOpen}>
          <MenuHandler>
            <Button
              variant="outlined"
              color="blue-gray"
              className="flex items-center justify-between gap-3 normal-case"
              onKeyDown={handleKeyDown}
            >
              <div className="flex items-center gap-2">
                <CurrentIcon className="h-5 w-5" />
                <span>{currentTab?.label}</span>
              </div>
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </MenuHandler>
          <MenuList>
            {tabs.map(({ value, label, Icon }) => (
              <MenuItem
                key={value}
                onClick={() => {
                  setTab(value);
                  setIsOpen(false);
                }}
                className="flex items-center gap-2"
              >
                <Icon className="h-5 w-5" />
                <span>{label}</span>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </div>
    </div>
  );
};

const PhoneNumbersPage = () => {
  // Initialize tab from sessionStorage or default to "submitted"
  const [tab, setTab] = useState<PhoneNumbersTabType>(() => {
    const savedTab = sessionStorage.getItem("phoneNumbersTab") as PhoneNumbersTabType;
    return savedTab && tabs.some(t => t.value === savedTab) ? savedTab : "submitted";
  });

  // Save tab to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem("phoneNumbersTab", tab);
  }, [tab]);

  const { data: rawData, isLoading } = useFetch(() => getPhoneNumbers(tab), [tab]);

  // Apply filters based on tab
  const data = rawData ? (tab === "submitted" ? rawData.filter(account => account.plan !== "free") : rawData) : null;

  return (
    <>
      <CardBackground />
      <Card className="mx-3 -mt-16 mb-6 border border-blue-gray-100 lg:mx-4">
        <CardBody className="p-4">
          <CustomDropdown
            tab={tab}
            setTab={setTab}
            tabs={tabs}
            subtitle={`${data?.length} accounts found`}
            title={tabs.find(t => t.value === tab)?.label || ""}
          />
          <PhoneNumbersTable tab={tab} data={data} isLoading={isLoading} />
        </CardBody>
      </Card>
    </>
  );
};

export default PhoneNumbersPage;
